/**
 * Teacher Slide View
 * Enhanced wrapper component for teacher's view of slides with analytics and controls
 * Designed for Junior High School classroom management with clear visual feedback
 */

import { useState, useEffect } from 'react';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  Activity, 
  CheckCircle, 
  Users, 
  Eye, 
  BarChart3,
  MessageSquare,
  StickyNote,
  Sparkles,
  XCircle,
  TrendingUp,
  Clock
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import type { Slide } from '../../../utils/supabase/client';
import { ContentSlideRenderer, ActivitySlideRenderer } from './index';
import { isContentSlideType, activityRendersOwnTitle } from '../../../utils/slides/slideRegistry';
import { stripHtml } from '../../../utils/slides/stripHtml';

interface TeacherSlideViewProps {
  slide: Slide;
  submissions?: any[];
  totalParticipants?: number;
  className?: string;
}

interface SubmissionSummary {
  option: string;
  count: number;
  percentage: number;
}

interface ResponseSubmission {
  student_name?: string | null;
}

export function getResponseCount(
  submissions: ReadonlyArray<ResponseSubmission>,
  totalParticipants: number
): number {
  const responderCount = new Set(
    submissions
      .map(submission => submission.student_name?.trim())
      .filter((studentName): studentName is string => Boolean(studentName))
  ).size;

  return Math.min(responderCount, Math.max(0, totalParticipants));
}

export default function TeacherSlideView({ 
  slide, 
  submissions = [],
  totalParticipants = 0,
  className = '' 
}: TeacherSlideViewProps) {
  const [submissionSummary, setSubmissionSummary] = useState<SubmissionSummary[]>([]);
  const isContentSlide = isContentSlideType(slide.type);
  
  // Calculate submission summary for poll questions
  useEffect(() => {
    if (slide.type === 'poll') {
      const summary = calculateSubmissionSummary(submissions, slide);
      setSubmissionSummary(summary);
    } else {
      setSubmissionSummary([]);
    }
  }, [submissions, slide]);

  const calculateSubmissionSummary = (subs: any[], slideData: Slide): SubmissionSummary[] => {
    const options = slideData.content?.options || [];
    const total = subs.length;

    if (total === 0) {
      return options.map((opt: string) => ({
        option: opt,
        count: 0,
        percentage: 0
      }));
    }

    // Count responses for each option
    const counts = options.map((opt: string, index: number) => {
      const count = subs.filter(sub => {
        const answer = sub.answer?.selectedOption ?? sub.answer?.answer;
        return answer === index || answer === opt;
      }).length;

      return {
        option: opt,
        count,
        percentage: Math.round((count / total) * 100)
      };
    });

    return counts;
  };

  const getResponseChartData = () => {
    if (submissionSummary.length === 0) return [];

    return submissionSummary.map((item, index) => ({
      name: item.option.length > 30 ? item.option.substring(0, 30) + '...' : item.option,
      value: item.count,
      percentage: item.percentage,
      color: ['#6366F1', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'][index % 6]
    }));
  };

  const getCorrectAnswerStats = () => {
    if (!slide.content?.correctAnswer) return null;

    const correctCount = submissions.filter(sub => {
      const answer = sub.answer?.selectedOption ?? sub.answer?.answer;
      return answer === slide.content.correctAnswer;
    }).length;

    return {
      correct: correctCount,
      incorrect: submissions.length - correctCount,
      percentage: submissions.length > 0 ? Math.round((correctCount / submissions.length) * 100) : 0
    };
  };

  const responseData = getResponseChartData();
  const correctStats = getCorrectAnswerStats();
  const responseCount = getResponseCount(submissions, totalParticipants);
  const responseRate = totalParticipants > 0 ? Math.round((responseCount / totalParticipants) * 100) : 0;

  const getResponseRateMessage = () => {
    if (totalParticipants === 0) return '';
    if (responseRate >= 90) return 'Excellent! Almost everyone responded!';
    if (responseRate >= 70) return 'Great participation from the class!';
    if (responseRate >= 50) return 'Good progress — encourage the rest!';
    if (responseRate >= 25) return 'Responses coming in...';
    if (responseCount > 0) return 'First responses coming in!';
    return '';
  };

  return (
    <div className={`${className}`}>
      {/* Teacher Control Bar - Enhanced with better visual hierarchy */}
      {!isContentSlide && (
        <div className="mb-5 bg-gradient-to-r from-indigo-50 via-violet-50 to-indigo-50 border border-indigo-200/50 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center">
                <Eye className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-sm">Teacher Dashboard</h3>
                <div className="text-[10px] text-muted-foreground">Real-time student responses</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 bg-white/80 rounded-lg px-3 py-1.5 border border-indigo-100">
                <Users className="w-4 h-4 text-indigo-500" />
                <span className="font-bold text-sm">{totalParticipants}</span>
                <span className="text-xs text-muted-foreground">students</span>
              </div>
              <div className="flex items-center gap-2 bg-white/80 rounded-lg px-3 py-1.5 border border-emerald-100">
                <Activity className="w-4 h-4 text-emerald-500" />
                <span className="font-bold text-sm">{responseCount}</span>
                <span className="text-xs text-muted-foreground">responded</span>
              </div>
            </div>
          </div>

          {/* Response Rate Progress - Enhanced */}
          {totalParticipants > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-indigo-700">Response Rate</span>
                  {getResponseRateMessage() && (
                    <span className="text-[10px] text-indigo-500 bg-indigo-100/50 px-2 py-0.5 rounded-full">
                      {getResponseRateMessage()}
                    </span>
                  )}
                </div>
                <span className="font-bold text-sm tabular-nums">
                  {responseCount}/{totalParticipants} ({responseRate}%)
                </span>
              </div>
              <div className="w-full h-3 bg-white/80 rounded-full overflow-hidden border border-indigo-100">
                <div 
                  className={`h-full rounded-full transition-all duration-700 ease-out ${
                    responseRate >= 80 ? 'bg-gradient-to-r from-emerald-400 to-emerald-500' :
                    responseRate >= 50 ? 'bg-gradient-to-r from-blue-400 to-indigo-500' :
                    responseRate >= 25 ? 'bg-gradient-to-r from-amber-400 to-orange-400' :
                    'bg-gradient-to-r from-gray-300 to-gray-400'
                  }`}
                  style={{ width: `${responseRate}%` }}
                />
              </div>
            </div>
          )}

          {/* Correct Answer Stats - Enhanced */}
          {correctStats && (
            <div className="mt-4 flex items-center gap-4">
              <div className="flex-1 flex items-center gap-3 bg-white/80 rounded-xl p-3 border border-emerald-100">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Correct</div>
                  <div className="font-bold text-emerald-600">{correctStats.correct} ({correctStats.percentage}%)</div>
                </div>
              </div>
              <div className="flex-1 flex items-center gap-3 bg-white/80 rounded-xl p-3 border border-red-100">
                <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                  <XCircle className="w-4 h-4 text-red-500" />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Incorrect</div>
                  <div className="font-bold text-red-500">{correctStats.incorrect}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Content slide minimal header */}
      {isContentSlide && (
        <div className="mb-4 flex items-center justify-center gap-2">
          <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 text-xs px-3 py-1 rounded-lg">
            <Eye className="w-3 h-3 mr-1.5" />
            Content Slide — Students see this too
          </Badge>
        </div>
      )}

      {/* Main Slide Content */}
      <div className="mb-6">
        {/* Slide Title & Question - for activity slides that don't render their own titles */}
        {!isContentSlide && !activityRendersOwnTitle(slide.type) && (
          <div className="text-center mb-6">
            {slide.content?.showTitle !== false && (
              <h2 className="text-3xl font-bold mb-2">
                {stripHtml(slide.content?.title) || 'Activity'}
              </h2>
            )}
            {slide.content?.question && (
              <div className="text-base sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-2">
                {stripHtml(slide.content.question)}
              </div>
            )}
            <div className="text-sm text-muted-foreground">
              Type: {slide.type.replace(/_/g, ' ')}
            </div>
          </div>
        )}

        {/* Render the slide */}
        {isContentSlide ? (
          <ContentSlideRenderer 
            slide={slide} 
            viewMode="teacher" 
          />
        ) : slide.type !== 'poll' ? (
          <ActivitySlideRenderer
            slide={slide}
            onSubmit={() => {}}
            onAnswerChange={() => {}}
            selectedAnswer={null}
            hasSubmitted={false}
            submissions={submissions}
            viewMode="teacher"
          />
        ) : null}
      </div>

      {/* Real-time Response Analytics Panel - Enhanced */}
      {slide.type === 'poll' && (
        <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-6 border border-border">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center">
                <BarChart3 className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-sm">Live Response Analytics</h3>
                <div className="text-[10px] text-muted-foreground">Updates automatically as students respond</div>
              </div>
            </div>
            {submissions.length > 0 && (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                <TrendingUp className="w-3 h-3 mr-1" />
                {submissions.length} total
              </Badge>
            )}
          </div>

          {/* Response Distribution Chart */}
          {responseData.length > 0 ? (
            <div>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={responseData} barCategoryGap="20%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis 
                    dataKey="name" 
                    fontSize={11}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    tick={{ fill: '#6b7280' }}
                  />
                  <YAxis fontSize={11} tick={{ fill: '#6b7280' }} />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-card border border-border rounded-xl p-3 shadow-xl">
                            <div className="font-semibold text-sm">{payload[0].payload.name}</div>
                            <div className="text-sm text-muted-foreground mt-1">
                              {payload[0].value} responses ({payload[0].payload.percentage}%)
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]} maxBarSize={60}>
                    {responseData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              
              {/* Response Summary Table - Enhanced */}
              <div className="mt-5 space-y-2">
                {submissionSummary.map((item, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-3 bg-white rounded-xl border border-border hover:border-indigo-200 transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div 
                        className="w-4 h-4 rounded-md flex-shrink-0"
                        style={{ backgroundColor: responseData[index]?.color }}
                      />
                      <span className="text-sm font-medium truncate">{item.option}</span>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs text-muted-foreground">{item.count} votes</span>
                      <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${item.percentage}%`, backgroundColor: responseData[index]?.color }}
                        />
                      </div>
                      <span className="text-sm font-bold min-w-[3rem] text-right tabular-nums">{item.percentage}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-10">
              <div className="w-14 h-14 rounded-2xl bg-indigo-100 flex items-center justify-center mx-auto mb-4">
                <Clock className="w-7 h-7 text-indigo-400" />
              </div>
              <div className="font-semibold text-sm mb-1">Waiting for student responses...</div>
              <div className="text-xs text-muted-foreground">Results will appear here in real-time as students answer</div>
            </div>
          )}

          {/* Collaborate Board Stats - Enhanced */}
          {slide.type === 'collaborate_board' && (() => {
            const allNotes: Array<{ content: string; author: string; color: string; avatar: string; id: string }> = 
              submissions.flatMap((s: any) => {
                const answer = s.answer;
                return answer?.notes ?? answer?.answer?.notes ?? [];
              });
            const notesByStudent: Record<string, number> = {};
            for (const note of allNotes) {
              notesByStudent[note.author] = (notesByStudent[note.author] || 0) + 1;
            }
            const contributorNames = Object.keys(notesByStudent);

            if (allNotes.length > 0) {
              return (
                <div className="mt-6 pt-6 border-t border-border">
                  <div className="flex items-center gap-2.5 mb-4">
                    <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                      <StickyNote className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-sm">Collaboration Stats</h4>
                      <div className="text-[10px] text-muted-foreground">Board activity overview</div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200/50 text-center">
                      <div className="text-2xl font-bold text-amber-600">{allNotes.length}</div>
                      <div className="text-xs text-amber-600/80 font-medium">Total Notes</div>
                    </div>
                    <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-4 border border-violet-200/50 text-center">
                      <div className="text-2xl font-bold text-violet-600">{contributorNames.length}</div>
                      <div className="text-xs text-violet-600/80 font-medium">Contributors</div>
                    </div>
                  </div>

                  <div>
                    <h5 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Top Contributors</h5>
                    <div className="space-y-1.5">
                      {contributorNames
                        .sort((a, b) => notesByStudent[b] - notesByStudent[a])
                        .slice(0, 5)
                        .map((name, i) => (
                          <div key={name} className="flex items-center justify-between p-2.5 bg-white rounded-xl border border-border">
                            <div className="flex items-center gap-2">
                              <span className="text-sm">{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i+1}`}</span>
                              <span className="text-sm font-medium">{name}</span>
                            </div>
                            <Badge variant="secondary" className="text-xs">{notesByStudent[name]} notes</Badge>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}
    </div>
  );
}
